import { useCallback, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
    UserFocusIcon,
    BarcodeIcon,
    TextAaIcon,
    FilmStripIcon,
    CaretDownIcon,
} from '@phosphor-icons/react';
import { Toggle, NumberStepper, NumberStepperType } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';

const MIN_FRAMES_PER_SECOND = 1;
const MAX_FRAMES_PER_SECOND = 30;

export interface AnalysisSettingsProps {
    faceRecognition: boolean;
    onFaceRecognitionChange: (checked: boolean) => void;
    licensePlateRecognition: boolean;
    onLicensePlateRecognitionChange: (checked: boolean) => void;
    opticalCharacterRecognition: boolean;
    onOpticalCharacterRecognitionChange: (checked: boolean) => void;
    analysisFramesPerSecond: number;
    onAnalysisFramesPerSecondChange: (value: number) => void;
}

export function AnalysisSettings({
    faceRecognition,
    onFaceRecognitionChange,
    licensePlateRecognition,
    onLicensePlateRecognitionChange,
    opticalCharacterRecognition,
    onOpticalCharacterRecognitionChange,
    analysisFramesPerSecond,
    onAnalysisFramesPerSecondChange,
}: AnalysisSettingsProps) {
    const { t } = useTranslation(I18nNamespace.COMMON);

    const handleAdvancedSettingsToggle = useCallback(
        (event: SyntheticEvent<HTMLDetailsElement>) => {
            const details = event.currentTarget;
            if (!details.open) return;
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    details.scrollIntoView({
                        behavior: 'smooth',
                        block: 'end',
                        inline: 'nearest',
                    });
                });
            });
        },
        [],
    );

    return (
        <>
            <div className="border border-grey-500 rounded-[5px] flex flex-col overflow-hidden">
                <div className="p-4">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1">
                            <div className="w-8 h-8 bg-grey-600 rounded-[4px] flex items-center justify-center flex-shrink-0">
                                <UserFocusIcon
                                    fill="currentColor"
                                    className="w-6 h-6 text-grey-200"
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm-medium text-white-100">
                                    {t('analysis.faceRecognition')}
                                </p>
                                <p className="text-xs-medium text-grey-300">
                                    {t('analysis.faceRecognitionDescription')}
                                </p>
                            </div>
                        </div>
                        <Toggle
                            id="face-recognition"
                            checked={faceRecognition}
                            onCheckedChange={onFaceRecognitionChange}
                        />
                    </div>
                </div>

                <div className="h-px w-full bg-grey-500" />

                <div className="p-4">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1">
                            <div className="w-8 h-8 bg-grey-600 rounded-[4px] flex items-center justify-center flex-shrink-0">
                                <BarcodeIcon
                                    fill="currentColor"
                                    className="w-6 h-6 text-grey-200"
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm-medium text-white-100">
                                    {t('analysis.licensePlateRecognition')}
                                </p>
                                <p className="text-xs-medium text-grey-300">
                                    {t(
                                        'analysis.licensePlateRecognitionDescription',
                                    )}
                                </p>
                            </div>
                        </div>
                        <Toggle
                            id="license-plate-recognition"
                            checked={licensePlateRecognition}
                            onCheckedChange={onLicensePlateRecognitionChange}
                        />
                    </div>
                </div>

                <div className="h-px w-full bg-grey-500" />

                <div className="p-4">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1">
                            <div className="w-8 h-8 bg-grey-600 rounded-[4px] flex items-center justify-center flex-shrink-0">
                                <TextAaIcon
                                    fill="currentColor"
                                    className="w-6 h-6 text-grey-200"
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm-medium text-white-100">
                                    {t('analysis.opticalCharacterRecognition')}
                                </p>
                                <p className="text-xs-medium text-grey-300">
                                    {t(
                                        'analysis.opticalCharacterRecognitionDescription',
                                    )}
                                </p>
                            </div>
                        </div>
                        <Toggle
                            id="optical-character-recognition"
                            checked={opticalCharacterRecognition}
                            onCheckedChange={
                                onOpticalCharacterRecognitionChange
                            }
                        />
                    </div>
                </div>
            </div>

            <details
                className="border border-grey-500 rounded-[5px] flex flex-col overflow-hidden group scroll-mb-8"
                onToggle={handleAdvancedSettingsToggle}
            >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4 [&::-webkit-details-marker]:hidden">
                    <span className="text-sm-medium text-white-100">
                        {t('analysis.advancedSettings')}
                    </span>
                    <CaretDownIcon
                        className="w-5 h-5 text-grey-300 shrink-0 transition-transform duration-200 group-open:rotate-180"
                        aria-hidden
                    />
                </summary>
                <div className="border-t border-grey-500">
                    <div className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-grey-600 rounded-[4px] flex items-center justify-center flex-shrink-0">
                                <FilmStripIcon
                                    fill="currentColor"
                                    className="w-6 h-6 text-grey-200"
                                />
                            </div>
                            <div className="flex flex-1 min-w-0 items-center justify-between gap-4">
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm-medium text-white-100">
                                        {t('analysis.analysisFramesPerSecond')}
                                    </p>
                                    <p className="text-xs-medium text-grey-300">
                                        {t(
                                            'analysis.analysisFramesPerSecondDescription',
                                        )}
                                    </p>
                                </div>
                                <NumberStepper
                                    type={NumberStepperType.NUMBER}
                                    value={analysisFramesPerSecond}
                                    onChange={onAnalysisFramesPerSecondChange}
                                    min={MIN_FRAMES_PER_SECOND}
                                    max={MAX_FRAMES_PER_SECOND}
                                    step={1}
                                    className="shrink-0"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </details>
        </>
    );
}
